import React, { useEffect, useState, useCallback } from "react";
import axios from "axios";
import VideoCard from "./VideoCard";
import { Link } from "react-router-dom";
import { toast } from "react-toastify";
import { API } from "../../services/api";
import SEO from "../../components/SEO";

const CATEGORIES = [
  { id: "Lecture", label: "Lectures", icon: "📚" },
  { id: "Interview", label: "Interviews", icon: "🎤" },
  { id: "Other", label: "Others", icon: "📹" }
];

const VideoSkeleton = () => (
  <div className="animate-pulse bg-gray-800/50 rounded-xl p-6 mb-6">
    <div className="h-6 bg-gray-700 rounded mb-3"></div>
    <div className="h-4 bg-gray-700 rounded mb-4 w-3/4"></div>
    <div className="aspect-video bg-gray-700 rounded-lg mb-4"></div>
    <div className="flex gap-2">
      <div className="h-8 bg-gray-700 rounded w-16"></div>
      <div className="h-8 bg-gray-700 rounded w-16"></div>
    </div>
  </div>
);

const GetVideo = () => {
  const [allVideos, setAllVideos] = useState([]);
  const [filteredVideos, setFilteredVideos] = useState([]);
  const [activeCategory, setActiveCategory] = useState("Lecture");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchVideos = useCallback(async () => {
    setLoading(true);
    setError(null);
    
    try {
      const res = await axios.get(API.GET_ALL_VIDEO, {
        withCredentials: true,
      });

      if (res.data?.success && res.data?.videos) {
        setAllVideos(res.data.videos);
        setFilteredVideos(res.data.videos.filter(video => video.category === "Lecture"));
      } else {
        throw new Error("Invalid response format");
      }
    } catch (err) {
      console.error("Error fetching videos:", err);
      setError(err.response?.data?.message || "Failed to load videos");
      toast.error("Failed to load videos. Please try again.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchVideos();
  }, [fetchVideos]);

  const handleCategoryChange = useCallback((category) => {
    setActiveCategory(category);
    const filtered = category === "All" 
      ? allVideos 
      : allVideos.filter(video => video.category === category);
    setFilteredVideos(filtered);
  }, [allVideos]);

  const handleVideoUpdate = useCallback(() => {
    fetchVideos(); // Refetch videos after update/delete
  }, [fetchVideos]);

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-gray-950 via-gray-900 to-gray-950 py-8 px-4">
        <div className="max-w-6xl mx-auto">
          <div className="animate-pulse">
            <div className="h-8 bg-gray-700 rounded mb-8 w-64"></div>
            <div className="flex gap-4 mb-8">
              {[1, 2, 3, 4].map(i => (
                <div key={i} className="h-10 bg-gray-700 rounded-full w-24"></div>
              ))}
            </div>
          </div>
          {[1, 2, 3].map(i => <VideoSkeleton key={i} />)}
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-gray-950 via-gray-900 to-gray-950 flex items-center justify-center px-4">
        <div className="text-center">
          <div className="text-6xl mb-4">⚠️</div>
          <h2 className="text-2xl font-bold text-white mb-4">Oops! Something went wrong</h2>
          <p className="text-gray-300 mb-6">{error}</p>
          <button 
            onClick={fetchVideos}
            className="bg-gradient-to-r from-indigo-600 via-purple-600 to-pink-600 hover:scale-[1.02] text-white px-6 py-3 rounded-lg transition-all duration-200 shadow-lg hover:shadow-indigo-500/30"
          >
            Try Again
          </button>
        </div>
      </div>
    );
  }

  return (
    <>
      <SEO
        title="Video Library - Admin Panel | ReferMe"
        description="View and manage your ReferMe video library."
        noindex={true}
        url="/videos"
      />

      <div className="min-h-screen bg-gradient-to-b from-gray-950 via-gray-900 to-gray-950 py-8 px-4">
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-8">
          <h1 className="text-3xl md:text-4xl font-bold text-white mb-4 sm:mb-0">
            Video Library
          </h1>
          <Link 
            to="/addvideo"
            className="inline-flex items-center gap-2 bg-gradient-to-r from-indigo-600 via-purple-600 to-pink-600 hover:scale-[1.02] text-white px-6 py-3 rounded-lg font-medium transition-all duration-200 shadow-lg hover:shadow-indigo-500/30"
          >
            <span className="text-xl">+</span>
            Add Video
          </Link>
        </div>

        {/* Category Filter */}
        <div className="mb-8">
          <div className="flex flex-wrap gap-3">
            <button
              onClick={() => handleCategoryChange("All")}
              className={`flex items-center gap-2 px-4 py-2 rounded-full font-medium transition-all duration-200 ${
                activeCategory === "All"
                  ? "bg-gradient-to-r from-indigo-500 via-purple-500 to-pink-500 text-white shadow-lg shadow-indigo-500/20"
                  : "bg-gray-800/50 border border-gray-700 text-white hover:bg-gray-800/70"
              }`}
            >
              <span>🎯</span>
              All ({allVideos.length})
            </button>
            {CATEGORIES.map(category => {
              const count = allVideos.filter(video => video.category === category.id).length;
              return (
                <button
                  key={category.id}
                  onClick={() => handleCategoryChange(category.id)}
                  className={`flex items-center gap-2 px-4 py-2 rounded-full font-medium transition-all duration-200 ${
                    activeCategory === category.id
                      ? "bg-gradient-to-r from-indigo-500 via-purple-500 to-pink-500 text-white shadow-lg shadow-indigo-500/20"
                      : "bg-gray-800/50 border border-gray-700 text-white hover:bg-gray-800/70"
                  }`}
                >
                  <span>{category.icon}</span>
                  {category.label} ({count})
                </button>
              );
            })}
          </div>
        </div>

        {/* Videos Grid */}
        {filteredVideos.length === 0 ? (
          <div className="text-center py-16">
            <div className="text-6xl mb-4">📹</div>
            <h3 className="text-xl font-semibold text-white mb-2">No videos found</h3>
            <p className="text-gray-400 mb-6">
              {activeCategory === "All" 
                ? "No videos have been uploaded yet." 
                : `No videos found in the ${activeCategory} category.`
              }
            </p>
            <Link 
              to="/addvideo"
              className="inline-flex items-center gap-2 bg-gradient-to-r from-indigo-600 via-purple-600 to-pink-600 hover:scale-[1.02] text-white px-6 py-3 rounded-lg transition-all duration-200 shadow-lg hover:shadow-indigo-500/30"
            >
              <span>+</span>
              Add First Video
            </Link>
          </div>
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {filteredVideos.map((video) => (
              <VideoCard 
                key={video._id} 
                data={video} 
                onUpdate={handleVideoUpdate}
              />
            ))}
          </div>
        )}
      </div>
    </div>
    </>
  );
};

export default GetVideo;
